/**
 * Shared session utilities for Cognito SDK.
 *
 * Provides getCurrentUser() and getAccessToken() functions that work with
 * the amazon-cognito-identity-js CognitoUserPool. Used by all three portals
 * to eliminate duplicated session-rehydration code.
 */

import { CognitoUserPool, CognitoUserSession } from 'amazon-cognito-identity-js'
import { User } from './types'

/**
 * Get the current authenticated user from a CognitoUserPool.
 *
 * Rehydrates the session from localStorage (via the Cognito SDK) and
 * extracts user info from the ID token. Returns null if there is no
 * valid session.
 */
export function getCurrentUser(userPool: CognitoUserPool): Promise<User | null> {
  const cognitoUser = userPool.getCurrentUser()
  if (!cognitoUser) {
    return Promise.resolve(null)
  }

  return new Promise((resolve) => {
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null)
        return
      }

      // Extract user info from the ID token directly. This avoids a
      // GetUser API call that requires the aws.cognito.signin.user.admin
      // scope — which OAuth/Google tokens don't have.
      const idToken = session.getIdToken()
      const payload = idToken.decodePayload()

      const email = payload.email as string | undefined
      const sub = payload.sub as string | undefined

      if (!email || !sub) {
        resolve(null)
        return
      }

      resolve({
        sub,
        email,
        name: (payload.name as string | undefined) || undefined,
        groups: (payload['cognito:groups'] as string[] | undefined) || [],
      })
    })
  })
}

/**
 * Get the access token JWT string from a CognitoUserPool session.
 *
 * Returns null if there is no valid session.
 */
export function getAccessToken(userPool: CognitoUserPool): Promise<string | null> {
  const cognitoUser = userPool.getCurrentUser()
  if (!cognitoUser) {
    return Promise.resolve(null)
  }

  return new Promise((resolve) => {
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null)
        return
      }

      resolve(session.getAccessToken().getJwtToken())
    })
  })
}
